import { Dispatch } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { Action, AnyAction, ThunkDispatch } from '@reduxjs/toolkit'

import {
  DestroyOtrEventPayload,
  OtrMessageEventPayload,
  OtrRequestedEventPayload,
  OtrRoomUpdatedEventPayload,
  OtrUserLeftRoomEventPayload,
} from 'types/otr.types'
import { FixMeLater } from 'types'
import { ERRORS } from 'locales/locales'
import { otrActions } from 'app/slices/otr.slice'
import { roomsActions } from 'app/slices/rooms.slice'
import { MessageUserType } from 'types/conversation.types'
import {  RoomData } from 'types/rooms.types'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { IUser, IUserData } from 'types/user.types'

type TFn = (key: any, options?: any) => string

export const otrEventHandlers = (
  currentUserId: string,
  rooms: RoomData,
  dispatch: ThunkDispatch<FixMeLater, undefined, AnyAction> & Dispatch<Action<FixMeLater>>,
  navigate: NavigateFunction,
  t: TFn,
) => {



  const closeOtrEverywhere = (roomId: string, opts?: { replace?: boolean; showMessage?: string }) => {
    if (!roomId) return

    if (opts?.showMessage) {
      DropdownAlert.ref.show({ message: opts.showMessage })
    }

    // OTR slice + rooms temizliği
    dispatch(otrActions.deleteOtrRoom({ roomId }))
    dispatch(roomsActions.removeConversation({ _id: roomId }))

    // Eğer şu an o odaya bakılıyorsa çık
    navigate('/messages', { replace: opts?.replace ?? true })
  }

  const markRoomDeletedToPreventSending = (roomId: string) => {
    // Destroy event alındıktan sonra mesaj gönderimini engellemek için room'u isDeleted yapıyoruz.
    dispatch(
      roomsActions.updateConversation({
        _id: roomId,
        members: [currentUserId],
        addRoomIfNotExist: false,
        isDeleted: true,
      }),
    )
  }

  return {
    onDestroyOtrRoom: ({ message, roomId }: DestroyOtrEventPayload) => {
      // mesaj gönderimini engelle
      markRoomDeletedToPreventSending(roomId)
      closeOtrEverywhere(roomId, { replace: true, showMessage: message });
    },
    onDestroyOtrRoomImmediately: ({ roomId, message }: { roomId: string; message: string }) => {
      // anında kapat
      markRoomDeletedToPreventSending(roomId)
      closeOtrEverywhere(roomId, { replace: true, showMessage: message })
    },
    onOtrRejected: ({ roomId }: { roomId: string }) => {
      closeOtrEverywhere(roomId, { replace: true })
      // Navbar "Messages" red-dot uses room.notificationCount
      dispatch(roomsActions.updateNotificationCount({ roomId, notificationCount: 0 }))
      DropdownAlert.ref.show({ isError: true, message: t(ERRORS.OTR_REJECTED_ERROR) })
    },  
    onOtrMessage: ({ message }: { message: OtrMessageEventPayload }) => {
      dispatch(otrActions.updateOtrMessage({
        roomId: message.roomId,
        cti: message.cti,
        message
      }))
    },
    onUserLeavesOtrRoom: ({ room, userId }: OtrUserLeftRoomEventPayload) => {
      // Print leave message.
      const user = room.users.find((user: IUser) => user._id === userId)
      dispatch(roomsActions.updateConversation(room))
      dispatch(
        otrActions.sendOtrMessage({
          roomId: room._id,
          type: ['text'],
          sender: MessageUserType.SYSTEM,
          content: {
            mentionedUsers: [userId],
            text: `@&_id:${userId}& OTR_LEFT`,
          },
          users: [
            {
              _id: userId,
              fullname: user?.fullname || '',
              status: user?.status || '',
              avatar: user?.avatar || '',
            },
          ],
        }),
      )
    },
    onOtrRoomUpdated: ({ room, joinedUser }: OtrRoomUpdatedEventPayload) => {
      dispatch(roomsActions.updateConversation(room))

      // ✅ createOtrRoom payload'unu senin slice'ının beklediği şekle göre gönder
      // (timeout vs kaldırıldı; createOtrRoom artık "idempotent" şekilde alanları tamamlıyor olmalı)
      dispatch(
        otrActions.createOtrRoom({
          roomId: room._id,
          requestSentAt: Date.now(),
          createdByUserId: room.createdByUserId,
          users: room?.users?.map((u: IUser) => u._id) || [],
          userData:
            room?.users?.reduce((acc: IUserData, u: IUser) => {
              acc[u._id] = u
              return acc
            }, {} as IUserData) || {},
        }),
      )

      // join system message
      if (joinedUser && joinedUser !== room.createdByUserId) {
        const user = room.users.find((u: IUser) => u._id === joinedUser)

        dispatch(
          otrActions.sendOtrMessage({
            type: ['text'],
            roomId: room._id,
            sender: MessageUserType.SYSTEM,
            content: {
              mentionedUsers: [joinedUser || currentUserId],
              text: `@&_id:${joinedUser || currentUserId}& OTR_JOINED`,
            },
            users: [
              {
                _id: joinedUser,
                fullname: user?.fullname || '',
                status: user?.status || '',
                avatar: user?.avatar || '',
              },
            ],
          }),
        )
      }
    },
    onOtrRequest: ({ room }: OtrRequestedEventPayload) => {
      // invited user: room'u ekle
      dispatch(roomsActions.createConversation(room))
      // Navbar "Messages" red-dot uses room.notificationCount
      dispatch(roomsActions.updateNotificationCount({ roomId: room._id, notificationCount: 1 }))

      const userData =
        room?.users?.reduce((acc: IUserData, user: IUser) => {
          acc[user._id] = user
          return acc
        }, {} as IUserData) || {}

      // slice state oluştur
      dispatch(
        otrActions.createOtrRoom({
          roomId: room._id,
          requestSentAt: Date.now(),
          createdByUserId: room.createdByUserId,
          users: room?.users?.map((u: IUser) => u._id) || [],
          userData,
        }),
      )

      // created system message
      const createdByUser = room?.users?.find((u: IUser) => u._id === room.createdByUserId)

      dispatch(
        otrActions.sendOtrMessage({
          type: ['text'],
          roomId: room._id,
          sender: MessageUserType.SYSTEM,
          content: {
            mentionedUsers: [room.createdByUserId],
            text: `@&_id:${room.createdByUserId}& OTR_CREATED`,
          },
          users: [
            {
              _id: room.createdByUserId,
              fullname: createdByUser?.fullname || '',
              status: createdByUser?.status || '',
              avatar: createdByUser?.avatar || '',
            },
          ],
        }),
      )
    },
   
  }
}
